/**
 * Admin → Legal → Documento (detalle)
 * Muestra metadata + contenido real de la última versión + timeline de versiones.
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { LegalMarkdown } from "@/components/legal/LegalMarkdown";
import {
  LEGAL_TYPE_LABELS,
  LegalBackLink,
  LegalVersionActions,
  VersionStatusBadge,
  pickLatestVersion,
  pickPublishedVersion,
} from "@/components/legal/legal-admin-shared";

export const Route = createFileRoute("/admin/ops/legal/documents/$docId/")({
  component: LegalDocumentDetailPage,
  head: () => ({ meta: [{ title: "Documento legal — Admin" }] }),
});

interface VersionRow {
  id: string;
  version: string;
  status: string;
  content: string;
  contentHash: string;
  createdAt: string;
  publishedAt: string | null;
  requiresReacceptance: boolean;
  summary: string | null;
}

interface DocDetail {
  id: string;
  type: string;
  title: string;
  status: string;
  updatedAt: string;
}

function LegalDocumentDetailPage() {
  const { docId } = Route.useParams();
  const [doc, setDoc] = useState<DocDetail | null>(null);
  const [versions, setVersions] = useState<VersionRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(() => {
    setLoading(true);
    fetch(`/api/admin/legal/${docId}`)
      .then(async (r) => {
        if (!r.ok) throw new Error("Documento no encontrado");
        return r.json() as Promise<{ document: DocDetail; versions: VersionRow[] }>;
      })
      .then((d) => {
        setDoc(d.document);
        setVersions(d.versions ?? []);
        setError(null);
        setLoading(false);
      })
      .catch(() => {
        setError("No se pudo cargar el documento");
        setLoading(false);
      });
  }, [docId]);

  useEffect(reload, [reload]);

  const latest = pickLatestVersion(versions);
  const published = pickPublishedVersion(versions);

  return (
    <div className="p-6">
      <LegalBackLink />

      {loading && <p className="text-sm text-muted-foreground">Cargando…</p>}
      {error && <p className="text-sm text-destructive">{error}</p>}

      {doc && (
        <>
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-foreground">{doc.title}</h1>
            <div className="mt-2 flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
              <span>
                Type: <code className="font-mono text-foreground">{doc.type}</code>
                {LEGAL_TYPE_LABELS[doc.type] ? ` · ${LEGAL_TYPE_LABELS[doc.type]}` : ""}
              </span>
              <span className="flex items-center gap-1.5">
                Estado: {latest ? <VersionStatusBadge status={latest.status} /> : <Badge variant="secondary">{doc.status}</Badge>}
              </span>
              <span>Última versión: {latest ? latest.version : "—"}</span>
              {published && (
                <span>Publicada: {published.version}</span>
              )}
            </div>
          </div>

          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="text-base">
                Contenido actual
                {latest ? ` — v${latest.version}` : ""}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {latest ? (
                <div data-testid="legal-document-content">
                  <LegalMarkdown content={latest.content} />
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">Este documento aún no tiene versiones.</p>
              )}
              <p className="mt-6 text-xs text-muted-foreground">
                El contenido de cada versión es inmutable una vez creado. Para cambiar el texto,
                crear una nueva versión desde Documentos.
              </p>
            </CardContent>
          </Card>

          <Card data-testid="legal-version-list">
            <CardHeader>
              <CardTitle className="text-base">Versiones</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {versions.map((v) => (
                <div
                  key={v.id}
                  className="flex items-center justify-between border-t border-border px-4 py-3"
                >
                  <div className="min-w-0">
                    <p className="font-medium text-foreground">v{v.version}</p>
                    <div className="mt-1 flex flex-wrap items-center gap-2">
                      <VersionStatusBadge status={v.status} />
                      <span className="text-xs text-muted-foreground">
                        {new Date(v.createdAt).toLocaleString("es-AR")}
                      </span>
                      {v.requiresReacceptance && (
                        <Badge variant="outline" className="border-amber-400 text-amber-700 text-xs">
                          Requiere reacceptance
                        </Badge>
                      )}
                    </div>
                  </div>
                  <div className="ml-4 flex items-center gap-2">
                    <Link
                      to="/admin/ops/legal/documents/$docId/versions/$versionId"
                      params={{ docId: doc.id, versionId: v.id }}
                      search={{} as never}
                    >
                      <Button size="sm" variant="outline">Ver versión</Button>
                    </Link>
                    <LegalVersionActions docId={doc.id} version={v} onDone={reload} />
                  </div>
                </div>
              ))}
              {versions.length === 0 && (
                <p className="px-4 py-6 text-center text-sm text-muted-foreground">Sin versiones</p>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
